import React, { useState } from 'react';
import { X, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useSystemAlerts } from '../../contexts/SystemAlertContext';
import { SystemAlert } from '../../services/systemStatusService';

/**
 * SystemAlertBar — top-of-page banner for SERVICE OUTAGES only.
 *
 * Renders only `error`-tier alerts (= a service is genuinely `down` and
 * the user can't do something they'd reasonably try). `warning`-tier
 * (degraded/slow) alerts deliberately don't render here — they live in
 * the always-on HealthPill in the Header so they don't shove the whole
 * dashboard down ~40px every time AI gets a bit slow.
 *
 * Multiple outages collapse into ONE row: "2 services down · click for
 * details" rather than stacking N full-width banners. The expanded view
 * lists each affected service with its own message + dismiss control.
 *
 * Hidden on /login, /register, /onboarding via RouteAwareAlertBar in
 * App.tsx — never disrupts conversion-critical flows.
 */
const SystemAlertBar: React.FC = () => {
  const { alerts, dismissAlert } = useSystemAlerts();
  const [expanded, setExpanded] = useState(false);

  // Only `error` (= service `down`) renders as a banner. `warning`
  // (degraded) is shown by the HealthPill in the header instead.
  const blockingAlerts = alerts.filter((a) => a.type === 'error');

  if (blockingAlerts.length === 0) {
    return null;
  }

  const summary =
    blockingAlerts.length === 1
      ? blockingAlerts[0].message
      : `${blockingAlerts.length} services are temporarily unavailable`;

  const showExpander = blockingAlerts.length > 1;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60]">
      <div className="bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3 min-w-0">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-medium truncate">{summary}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 ml-4">
              {showExpander && (
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="px-2 py-1 hover:bg-white/15 rounded transition-colors text-xs font-medium inline-flex items-center gap-1"
                  aria-expanded={expanded}
                  aria-label={expanded ? 'Hide details' : 'Show details'}
                >
                  {expanded ? 'Hide' : 'Details'}
                  {expanded ? (
                    <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                  )}
                </button>
              )}
              {!showExpander && blockingAlerts[0].dismissible !== false && (
                <button
                  onClick={() => dismissAlert(blockingAlerts[0].id)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                  aria-label="Dismiss alert"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Expanded detail rows — only when >1 outage and user clicked Details */}
        {showExpander && expanded && (
          <div className="border-t border-white/15">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 space-y-1">
              {blockingAlerts.map((alert: SystemAlert) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between text-sm"
                >
                  <p className="flex-1 min-w-0 truncate">{alert.message}</p>
                  {alert.dismissible !== false && (
                    <button
                      onClick={() => dismissAlert(alert.id)}
                      className="p-1 hover:bg-white/20 rounded transition-colors ml-3 flex-shrink-0"
                      aria-label={`Dismiss ${alert.service ?? 'alert'}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemAlertBar;
